import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../../services/supabase';
import type { News } from '../../types/models';
import { applyAdminFilter, fixTypes } from '../../services/adminPermissions';

interface NewsFormData {
  message: string;
}

const NewsAdmin: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<NewsFormData>({
    message: ''
  });
  const [currentNewsId, setCurrentNewsId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    try {
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let queryResult;
      
      if (superAdmin) {
        // Superadmins see all news
        queryResult = await supabase
          .from('news')
          .select('*')
          .order('published_at', { ascending: false });
      } else {
        // Regular admins might see only their club's news
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          queryResult = { data: [], error: null };
        } else {
          try {
            // Show all news for now since club_id doesn't exist on the news table yet
            queryResult = await supabase
              .from('news')
              .select('*')
              .order('published_at', { ascending: false });
          } catch (e) {
            // If club_id doesn't exist on this table, show all news
            queryResult = await supabase
              .from('news')
              .select('*')
              .order('published_at', { ascending: false });
          }
        }
      }

      const { data, error } = queryResult;
      
      if (error) throw error;
      
      // Fix types to ensure proper typing
      const typedData = fixTypes<News>(data || []);
      setNews(typedData);
    } catch (error: any) {
      console.error('Error fetching news:', error.message);
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (newsItem?: News) => {
    if (newsItem) {
      setFormData({
        message: newsItem.message
      });
      setCurrentNewsId(newsItem.id);
      setCharCount(newsItem.message.length);
    } else {
      setFormData({
        message: ''
      });
      setCurrentNewsId(null);
      setCharCount(0);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'message') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.message.trim()) {
        showSnackbar('News message cannot be empty', 'error');
        return;
      }

      if (formData.message.length > 1000) {
        showSnackbar('Message must be 1000 characters or less', 'error');
        return;
      }
      
      // Get current user role to check for club_id
      const { getCurrentUserRole } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      
      // Prepare news data with club_id if applicable
      const newsData: any = {
        message: formData.message.trim(),
        published_at: new Date().toISOString()
      };
      
      // Commented out for now until the schema is updated
      // if (userRole?.club_id) {
      //   newsData.club_id = userRole.club_id;
      // }

      if (currentNewsId) {
        // Update existing news
        // First check if the admin has permission to update this news item
        let updateQuery = supabase
          .from('news')
          .update(newsData);
          
        // Commented out for now until the schema is updated
        // if (userRole?.role !== 'SUPERADMIN' && userRole?.club_id) {
        //   updateQuery = updateQuery.eq('club_id', userRole.club_id);
        // }
        
        const { data, error } = await updateQuery.eq('id', currentNewsId);

        if (error) throw error;
        showSnackbar('News updated successfully', 'success');
      } else {
        // Add new news
        const { data, error } = await supabase
          .from('news')
          .insert([newsData]);

        if (error) throw error;
        showSnackbar('News added successfully', 'success');
      }

      handleCloseDialog();
      fetchNews();
    } catch (error: any) {
      console.error('Error saving news:', error.message);
      showSnackbar(error.message, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      try {
        // Get current user role to check permissions
        const { getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
        const userRole = await getCurrentUserRole();
        const superAdmin = await isSuperAdmin();
        
        // Build the delete query
        let query = supabase
          .from('news')
          .delete();
        
        // Add appropriate filters
        // Only allow deleting if superadmin or if the news item belongs to the admin's club
        if (!superAdmin && userRole?.club_id) {
          query = query.eq('club_id', userRole.club_id);
        }
        
        // Add the final ID filter
        const { error } = await query.eq('id', id);

        if (error) throw error;
        showSnackbar('News deleted successfully', 'success');
        fetchNews();
      } catch (error: any) {
        console.error('Error deleting news:', error.message);
        showSnackbar(error.message, 'error');
      }
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="div">
          Manage News
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add News
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : news.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="60%">Message</TableCell>
                <TableCell>Published</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {news.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.message.length > 100 
                      ? `${item.message.substring(0, 100)}...` 
                      : item.message}
                  </TableCell>
                  <TableCell>{formatDate(item.published_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenDialog(item)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(item.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          No news items found. Add your first news item using the button above.
        </Typography>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentNewsId ? 'Edit News' : 'Add News'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="message"
            label="News Message"
            type="text"
            fullWidth
            multiline
            rows={5}
            value={formData.message}
            onChange={handleInputChange}
            variant="outlined"
            error={charCount > 1000}
            helperText={`${charCount}/1000 characters${charCount > 1000 ? ' (maximum exceeded)' : ''}`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.message.trim() || charCount > 1000}
          >
            {currentNewsId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsAdmin;